import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { theme } from '@resistapp/client/components/shared/theme';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { getLoginPathForLocation } from '@resistapp/client/utils/login-return-path';
import type { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';

import { faSquare as faSquareRegular } from '@fortawesome/free-regular-svg-icons';
import { faCaretRight, faCheck, faCheckDouble } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isProviderAdminUser, isResistomapAdminUser } from '@resistapp/client/utils/access';
import { allLabFeatures, getUserLabFeatures, LabFeature } from '@resistapp/common/features';
import { GenePanelState, LabWorkbookStatus, OrganisationType } from '@resistapp/common/types';
import { useLabWorkbook, type LabWorkbookSummary } from './api/use-lab-workbook';
import { LabWorkbookAdminModal } from './components/lab-workbook-admin-modal';
import { getDnaQaIconVariant, getSampleSpecIconVariant, getSmartChipRunIconVariant } from './lab-layout-utils';
import { deriveReadOnlyState, isStatusAtLeast } from './status-utils';

type LabLayoutParams = {
  workbookId?: string;
};

type ViewerAccess = {
  canViewSpec: boolean;
  canViewDnaQc: boolean;
  canViewPrimerPrep: boolean;
  canViewSamplePrep: boolean;
  canViewRun: boolean;
  hasFullAccess: boolean;
  isAssignedLabMember: boolean;
  labFeatures: LabFeature[];
  isResistomapAdmin: boolean;
  isProviderAdmin: boolean;
  isCustomerOnly: boolean;
};

type LabStep = {
  id?: LabStepId;
  index: number;
  label: string;
  pathSuffix: string;
  search?: string;
};

export type LabStepId = 'gene-template' | 'sample-spec' | 'dna-qa' | 'primer-prep' | 'sample-prep' | 'smartchip-run';
type StepCompletionRule = { type: 'status'; status: LabWorkbookStatus };

type StepDefinition = LabStep & {
  id: LabStepId;
  requiredFeature: LabFeature;
  completionRule: StepCompletionRule;
};

type StepState = StepDefinition & {
  href: string;
  canAccess: boolean;
  isComplete: boolean;
};

const stepDefinitions: StepDefinition[] = [
  {
    id: 'gene-template',
    index: 1,
    label: 'Assay Template',
    pathSuffix: 'gene-template',
    search: 'mode=genes',
    requiredFeature: LabFeature.GENE_TEMPLATE,
    completionRule: { type: 'status', status: LabWorkbookStatus.SAMPLE_SELECTION },
  },
  {
    id: 'sample-spec',
    index: 2,
    label: 'Sample Spec',
    pathSuffix: 'sample-spec',
    requiredFeature: LabFeature.SAMPLE_SPEC,
    completionRule: { type: 'status', status: LabWorkbookStatus.SUBMITTED },
  },
  {
    id: 'dna-qa',
    index: 3,
    label: 'DNA QC',
    pathSuffix: 'dna-qa',
    requiredFeature: LabFeature.DNA_QC,
    completionRule: { type: 'status', status: LabWorkbookStatus.MEASURED },
  },
  {
    id: 'primer-prep',
    index: 4,
    label: 'Primer prep',
    pathSuffix: 'primer-prep',
    requiredFeature: LabFeature.PRIMER_PREP,
    completionRule: { type: 'status', status: LabWorkbookStatus.PRIMERS_PREPPED },
  },
  {
    id: 'sample-prep',
    index: 5,
    label: 'Sample prep',
    pathSuffix: 'sample-plates',
    requiredFeature: LabFeature.SAMPLE_PREP,
    completionRule: { type: 'status', status: LabWorkbookStatus.SAMPLES_PREPPED },
  },
  {
    id: 'smartchip-run',
    index: 6,
    label: 'SmartChip run',
    pathSuffix: 'smartchip-run',
    requiredFeature: LabFeature.SMARTCHIP,
    completionRule: { type: 'status', status: LabWorkbookStatus.RUNNING },
  },
];

function isStepComplete(step: StepDefinition, workbookData: LabWorkbookSummary | undefined): boolean {
  if (!workbookData) {
    return false;
  }
  if (step.id === 'gene-template') {
    return (
      isStatusAtLeast(workbookData.workbook.status, LabWorkbookStatus.SAMPLE_SELECTION) &&
      hasValidLinkedGenePanel(workbookData)
    );
  }

  switch (step.completionRule.type) {
    case 'status':
      return isStatusAtLeast(workbookData.workbook.status, step.completionRule.status);
    default:
      return false;
  }
}

function buildStepHref(workbookId: string, step: StepDefinition): string {
  const base = `/lab/${workbookId}/${step.pathSuffix}`;
  if (!step.search) {
    return base;
  }
  return `${base}?${step.search}`;
}

function getCurrentStepId(pathname: string, search: string): LabStepId | null {
  const segments = pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  if (!lastSegment) {
    return null;
  }
  if (lastSegment === 'gene-template') {
    return 'gene-template';
  }
  if (lastSegment === 'sample-spec') {
    return 'sample-spec';
  }
  if (lastSegment === 'dna-extraction' || lastSegment === 'dna-qa') {
    return 'dna-qa';
  }
  if (lastSegment === 'smartchip-spec') {
    const params = new URLSearchParams(search);
    return params.get('mode') === 'genes' ? 'gene-template' : 'sample-spec';
  }
  if (lastSegment === 'primer-prep') {
    return 'primer-prep';
  }
  if (lastSegment === 'sample-plates') {
    return 'sample-prep';
  }
  if (lastSegment === 'smartchip-run') {
    return 'smartchip-run';
  }
  if (segments.includes('smartchip-run')) {
    return 'smartchip-run';
  }
  return null;
}

function buildStepStates(
  workbookId: string | undefined,
  viewerAccess: ViewerAccess,
  workbookData: LabWorkbookSummary | undefined,
): StepState[] {
  if (!workbookId) {
    return [];
  }
  const labFeatureSet = new Set(viewerAccess.labFeatures);
  const hideDnaQc =
    workbookData?.workbook.status === LabWorkbookStatus.LEGACY || Boolean(workbookData?.legacyImport.imported);
  return stepDefinitions
    .filter(step => !(hideDnaQc && step.id === 'dna-qa'))
    .map(step => ({
      ...step,
      href: buildStepHref(workbookId, step),
      canAccess: viewerAccess.hasFullAccess || labFeatureSet.has(step.requiredFeature),
      isComplete: isStepComplete(step, workbookData),
    }));
}

function resolveFallbackStep(stepStates: StepState[]): StepState | null {
  const accessible = stepStates.filter(step => step.canAccess);
  if (accessible.length === 0) {
    const geneStep = stepStates.find(step => step.id === 'gene-template');
    if (geneStep) {
      return geneStep;
    }
    return stepStates.length > 0 ? stepStates[0] : null;
  }
  const firstIncomplete = accessible.find(step => !step.isComplete);
  return firstIncomplete ?? accessible[accessible.length - 1];
}

export function LabLayout() {
  const { workbookId } = useParams<LabLayoutParams>();
  const { user, isAdmin, userLoading } = useUser();
  const navigate = useNavigateWithQuery();
  const editModalDisclosure = useDisclosure();
  const location = useLocation();
  const loginPath = getLoginPathForLocation(location);
  const workbookQuery = useLabWorkbook(user ? (workbookId ?? null) : null);
  const workbookData = workbookQuery.data;
  const workbookStatus = workbookData?.workbook.status ?? null;
  const readOnlyState = deriveReadOnlyState(workbookStatus);

  const viewerAccess = useMemo<ViewerAccess>(() => {
    if (!workbookData) {
      return {
        canViewSpec: true,
        canViewDnaQc: Boolean(isAdmin),
        canViewPrimerPrep: Boolean(isAdmin),
        canViewSamplePrep: Boolean(isAdmin),
        canViewRun: Boolean(isAdmin),
        hasFullAccess: Boolean(isAdmin),
        isAssignedLabMember: false,
        labFeatures: [...allLabFeatures],
        isResistomapAdmin: Boolean(isAdmin),
        isProviderAdmin: Boolean(isAdmin),
        isCustomerOnly: false,
      };
    }

    const ownerOrganisationId = workbookData.sampleSheet.organisationId;
    const assignedLabOrganisationId =
      workbookData.assignedLabOrganisation?.id ?? workbookData.workbook.assignedLabOrganisationId ?? null;

    const userOrganisations = user?.organisations ?? [];
    const isCustomerOnly = userOrganisations.length > 0 && userOrganisations.every(org => org.type === OrganisationType.CUSTOMER);
    const organisationIds = new Set(userOrganisations.map(org => org.id));
    const isResistomapStaff = userOrganisations.some(org => org.type === OrganisationType.RESISTOMAP);
    const isAssignedLabMember = assignedLabOrganisationId != null && organisationIds.has(assignedLabOrganisationId);
    const isOwnerMember = organisationIds.has(ownerOrganisationId);
    const isRootAdmin = isResistomapAdminUser(user);
    const isProviderAdmin = isProviderAdminUser(user);

    const hasFullAccess = Boolean(isAdmin) || isRootAdmin || isResistomapStaff;

    const labFeatureSet = new Set<LabFeature>(hasFullAccess ? allLabFeatures : getUserLabFeatures(user));
    const canViewGeneTemplate = hasFullAccess || labFeatureSet.has(LabFeature.GENE_TEMPLATE) || isOwnerMember;
    const canViewSampleSpec = hasFullAccess || labFeatureSet.has(LabFeature.SAMPLE_SPEC) || isOwnerMember;
    const canViewDnaQc = hasFullAccess || labFeatureSet.has(LabFeature.DNA_QC);
    const canViewPrimerPrep = hasFullAccess || labFeatureSet.has(LabFeature.PRIMER_PREP);
    const canViewSamplePrep = hasFullAccess || labFeatureSet.has(LabFeature.SAMPLE_PREP);
    const canViewRun = hasFullAccess || labFeatureSet.has(LabFeature.SMARTCHIP);

    return {
      canViewSpec: canViewGeneTemplate || canViewSampleSpec,
      canViewDnaQc,
      canViewPrimerPrep,
      canViewSamplePrep,
      canViewRun,
      hasFullAccess,
      isAssignedLabMember,
      labFeatures: Array.from(labFeatureSet),
      isResistomapAdmin: isRootAdmin,
      isProviderAdmin,
      isCustomerOnly,
    };
  }, [isAdmin, user, workbookData]);

  const stepStates = useMemo<StepState[]>(
    () => buildStepStates(workbookId, viewerAccess, workbookData),
    [viewerAccess, workbookData, workbookId],
  );

  const shouldHideAdvancedSteps = useMemo(() => {
    const organisations = user?.organisations ?? [];
    if (organisations.length === 0) {
      return false;
    }
    return organisations.every(org => org.type === OrganisationType.CUSTOMER);
  }, [user]);

  const progressStepStates = useMemo(
    () =>
      shouldHideAdvancedSteps
        ? stepStates.filter(
            step => step.id === 'gene-template' || step.id === 'sample-spec' || (step.id === 'dna-qa' && step.canAccess),
          )
        : stepStates,
    [shouldHideAdvancedSteps, stepStates],
  );

  const currentStepId = useMemo(
    () => getCurrentStepId(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const currentStep = stepStates.find(step => step.id === currentStepId) ?? null;
  const isCurrentStepAllowed = currentStepId == null ? true : Boolean(currentStep?.canAccess);
  const hasAnyAccess = stepStates.some(step => step.canAccess);
  const accessibleSteps = useMemo<LabStep[]>(
    () =>
      stepStates
        .filter(step => step.canAccess)
        .map(({ id, index, label, pathSuffix, search }) => ({ id, index, label, pathSuffix, search })),
    [stepStates],
  );

  const organisationLinkLabel = useMemo(() => {
    if (!isAdmin || !workbookData) {
      return null;
    }
    const name = workbookData.sampleSheet.organisation.name;
    const short = name.length > 24 ? `${name.slice(0, 24)}…` : name;
    return `← ${short}`;
  }, [isAdmin, workbookData]);

  const hasLegacyImportPermission = useMemo(
    () => Boolean(isAdmin && viewerAccess.hasFullAccess && workbookData?.legacyImport.eligible),
    [isAdmin, viewerAccess.hasFullAccess, workbookData?.legacyImport.eligible],
  );

  const showLegacyImportInline = hasLegacyImportPermission && currentStepId === 'gene-template';
  const canEditWorkbook = viewerAccess.isResistomapAdmin || viewerAccess.isProviderAdmin;

  const progressItems = progressStepStates.flatMap((step, index) => {
    const nodes = [
      <StepIndicator key={step.id} step={step} isCurrent={step.id === currentStepId} workbookData={workbookData} />,
    ];
    if (index < progressStepStates.length - 1) {
      const nextStep = progressStepStates[index + 1];
      const isArrowToCurrent = nextStep.id === currentStepId;
      const sepColor = isArrowToCurrent
        ? theme.colors.blue900
        : step.canAccess
          ? theme.colors.neutral900
          : theme.colors.neutral500;
      nodes.push(
        <FontAwesomeIcon
          key={`${step.id}-separator`}
          icon={faCaretRight}
          style={{ color: sepColor, fontSize: 18, marginInline: 6 }}
        />,
      );
    }
    return nodes;
  });

  // Legacy import completion is handled at page-level header controls where shown.

  if (userLoading || user === undefined) {
    return (
      <Center minH="240px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to={loginPath} replace />;
  }

  if (!workbookId) {
    return (
      <div style={{ padding: theme.spacing[6] }}>
        <p style={{ color: theme.colors.neutral700 }}>Missing workbook identifier in URL.</p>
      </div>
    );
  }

  return (
    <VStack align="stretch" spacing={theme.spacing[6]} padding={theme.spacing[6]}>
      <Flex
        align="center"
        flexWrap="wrap"
        gap={theme.spacing[2]}
        style={{
          borderBottom: `1px solid ${theme.colors.neutral300}`,
          paddingBottom: theme.spacing[2],
        }}
      >
        {organisationLinkLabel && workbookData ? (
          <NavLink
            to={`/admin/organisation/${workbookData.sampleSheet.organisation.id}`}
            style={() => ({
              display: 'inline-block',
              padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
              borderRadius: 6,
              textDecoration: 'none',
              color: theme.colors.neutral700,
              backgroundColor: theme.colors.neutral100,
              fontWeight: 500,
            })}
            aria-label="Back to organisation"
          >
            {organisationLinkLabel}
          </NavLink>
        ) : null}

        {organisationLinkLabel ? (
          <Text color={theme.colors.neutral300} fontWeight={600}>
            |
          </Text>
        ) : null}

        <Flex align="center" flexWrap="wrap" gap={theme.spacing[2]} flex="1 1 auto" minWidth={0}>
          {progressItems}
        </Flex>

        {canEditWorkbook && workbookData ? (
          <Button size="sm" colorScheme="blue" flexShrink={0} onClick={editModalDisclosure.onOpen}>
            Edit workbook
          </Button>
        ) : null}
      </Flex>

      {canEditWorkbook && workbookData ? (
        <LabWorkbookAdminModal
          mode="edit"
          workbookId={workbookId}
          organisationId={workbookData.sampleSheet.organisationId}
          canManageShipping={viewerAccess.isResistomapAdmin}
          isOpen={editModalDisclosure.isOpen}
          onClose={editModalDisclosure.onClose}
          initialMetadata={{
            name: workbookData.workbook.name ?? null,
            projectLead: workbookData.workbook.projectLead ?? null,
            channelUrl: workbookData.workbook.channelUrl ?? null,
          }}
          resetEligibility={workbookData.resetEligibility}
          deleteEligibility={workbookData.deleteEligibility}
          onReset={() => {
            void workbookQuery.refetch();
          }}
          onDeleted={() => {
            navigate('/admin/lab-workbooks', false, true);
          }}
        />
      ) : null}

      {/* Legacy lab sheet import control is now shown in page headers for Assay Template and Sample Spec steps. */}

      {!hasAnyAccess ? (
        <Box padding={theme.spacing[4]} color={theme.colors.neutral700}>
          You do not have access to this lab workbook.
        </Box>
      ) : !isCurrentStepAllowed ? (
        <Box padding={theme.spacing[4]} color={theme.colors.neutral700}>
          You do not have access to this step.
        </Box>
      ) : (
        <Box minWidth={0} width="100%">
          <Outlet
            context={{
              workbookId,
              steps: accessibleSteps,
              workbookQuery,
              viewerAccess,
              currentStepId,
              showLegacyImportInline,
              readOnly: readOnlyState,
            }}
          />
        </Box>
      )}
    </VStack>
  );
}

type StepIndicatorProps = {
  step: StepState;
  isCurrent: boolean;
  workbookData?: LabWorkbookSummary;
};

function hasValidLinkedGenePanel(workbookData?: LabWorkbookSummary): boolean {
  const state = workbookData?.linkedGenePanel?.state ?? null;
  return state === GenePanelState.VALID || state === GenePanelState.LOCKED;
}

function getIconVariantForStep(stepId: LabStepId, workbookData?: LabWorkbookSummary): 'none' | 'first' | 'both' {
  const status = workbookData?.workbook.status ?? null;
  switch (stepId) {
    case 'gene-template': {
      if (isStatusAtLeast(status, LabWorkbookStatus.SAMPLE_SELECTION)) {
        return 'both';
      }
      if (status === LabWorkbookStatus.DRAFT && hasValidLinkedGenePanel(workbookData)) {
        return 'first';
      }
      return 'none';
    }
    case 'sample-spec': {
      const submitted = workbookData?.sampleProgress.submitted ?? 0;
      const minReq = workbookData?.sampleProgress.minimumRequired ?? null;
      return getSampleSpecIconVariant({
        status,
        submitted,
        minimumRequired: minReq,
      });
    }
    case 'primer-prep':
      return isStatusAtLeast(status, LabWorkbookStatus.PRIMERS_PREPPED) ? 'both' : 'none';
    case 'dna-qa':
      return getDnaQaIconVariant({
        status,
        hasMeasurements: workbookData?.dnaProgress.hasMeasurements,
        submitted: workbookData?.sampleProgress.submitted,
        minimumRequired: workbookData?.sampleProgress.minimumRequired,
      });
    case 'sample-prep':
      return isStatusAtLeast(status, LabWorkbookStatus.SAMPLES_PREPPED) ? 'both' : 'none';
    case 'smartchip-run': {
      return getSmartChipRunIconVariant({
        chips: workbookData?.smartChip?.chips,
        expectedChipCount: workbookData?.smartChip?.chipsToRun ?? workbookData?.workbook.numChips,
      });
    }
    default:
      return 'none';
  }
}

function getStepLabelColor(step: StepState, isCurrent: boolean): string {
  return step.canAccess ? (isCurrent ? theme.colors.blue900 : theme.colors.neutral900) : theme.colors.neutral500;
}

function StepIndicator({ step, isCurrent, workbookData }: StepIndicatorProps) {
  const labelColor = getStepLabelColor(step, isCurrent);
  const fontWeight = isCurrent ? 600 : 500;
  const fontSize = theme.fontSize.large; // make steps a bit bigger
  const opacity = step.canAccess ? 1 : 0.6;
  const iconVariant = getIconVariantForStep(step.id, workbookData);

  const icon = iconVariant === 'both' ? faCheckDouble : iconVariant === 'first' ? faCheck : faSquareRegular;
  const iconStyle = {
    fontSize: 18,
    backgroundColor: 'transparent',
    opacity: iconVariant === 'none' ? (isCurrent ? 1 : 0.1) : 1,
    color: iconVariant === 'none' && isCurrent ? labelColor : undefined,
  };

  const content = (
    <Flex
      as="span"
      align="center"
      gap={theme.spacing[1]}
      style={{
        display: 'inline-flex',
        color: labelColor,
        fontWeight,
        fontSize,
        cursor: step.canAccess ? 'pointer' : 'default',
        opacity,
      }}
    >
      <FontAwesomeIcon icon={icon} style={iconStyle} />
      <Text whiteSpace="nowrap" fontWeight="inherit">
        {step.label}
      </Text>
    </Flex>
  );

  if (!step.canAccess) {
    return <Box as="span">{content}</Box>;
  }

  return (
    <Link
      to={step.href}
      style={{ textDecoration: 'none', display: 'inline-flex' }}
      aria-current={isCurrent ? 'step' : undefined}
    >
      {content}
    </Link>
  );
}

export function LabWorkbookIndexRedirect() {
  const navigate = useNavigateWithQuery();
  const { workbookId, workbookQuery, viewerAccess } = useLabLayoutContext();
  const workbookData = workbookQuery.data;
  const { isLoading, isError, error } = workbookQuery;
  const stepStates = useMemo(
    () => buildStepStates(workbookId, viewerAccess, workbookData),
    [workbookData, viewerAccess, workbookId],
  );
  const fallbackStep = useMemo(() => resolveFallbackStep(stepStates), [stepStates]);

  useEffect(() => {
    if (!workbookId || !workbookData || !fallbackStep) {
      return;
    }
    navigate(fallbackStep.href, false, true);
  }, [fallbackStep, navigate, workbookData, workbookId]);

  if (isError) {
    const message = error instanceof Error ? error.message : 'Failed to load workbook';
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
    );
  }

  if (isLoading || !workbookId || !fallbackStep) {
    return (
      <Center minH="240px">
        <Spinner size="lg" />
      </Center>
    );
  }

  return null;
}

export type LabLayoutContext = {
  workbookId: string;
  steps: LabStep[];
  workbookQuery: UseQueryResult<LabWorkbookSummary>;
  viewerAccess: ViewerAccess;
  currentStepId: LabStepId | null;
  showLegacyImportInline: boolean;
  readOnly: ReturnType<typeof deriveReadOnlyState>;
};

export function useLabLayoutContext(): LabLayoutContext {
  return useOutletContext<LabLayoutContext>();
}
